import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import { startOfWeek, format } from 'date-fns';
import { parseCalendarDate } from '../utils/dateTime';
import {
  ROLLUP_ACTIVITY_FIELDS,
  STUDY_METRIC_FIELDS,
  getMessageFieldValue,
  getActivityCount,
  getBinKey,
  enumerateBins,
  getDataTypeLabel,
} from '../utils/chartBinning';
import DrillDownModal from './DrillDownModal';
import './DataChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  zoomPlugin
);

const FACILITY_COLORS = [
  { border: 'rgb(20, 184, 166)', background: 'rgba(20, 184, 166, 0.15)' },
  { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.1)' },
  { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.1)' },
  { border: 'rgb(255, 206, 86)', background: 'rgba(255, 206, 86, 0.1)' },
  { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.1)' },
  { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.1)' },
  { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.1)' },
  { border: 'rgb(199, 199, 199)', background: 'rgba(199, 199, 199, 0.1)' },
];

const MESSAGE_TYPE_FIELDS = [
  { field: 'referral_messages_sent', label: 'Referral', border: 'rgb(20, 184, 166)', background: 'rgba(20, 184, 166, 0.15)' },
  { field: 'vmail_messages_Sent', label: 'VMail', border: 'rgb(16, 185, 129)', background: 'rgba(16, 185, 129, 0.1)' },
  { field: 'vchat_messages_Sent', label: 'VChat Sent', border: 'rgb(245, 158, 11)', background: 'rgba(245, 158, 11, 0.1)' },
  { field: 'vchat_messages_received', label: 'VChat Received', border: 'rgb(239, 68, 68)', background: 'rgba(239, 68, 68, 0.1)' },
  { field: 'vround_messages_sent', label: 'VRounds', border: 'rgb(139, 92, 246)', background: 'rgba(139, 92, 246, 0.1)' },
  { field: 'vtransfer_messages_sent', label: 'VTransfer', border: 'rgb(236, 72, 153)', background: 'rgba(236, 72, 153, 0.1)' },
  { field: 'Other_messages_sent', label: 'Other', border: 'rgb(156, 163, 175)', background: 'rgba(156, 163, 175, 0.1)' },
];

function sumMessageField(records, field) {
  return (records || []).reduce((sum, r) => {
    const d = r.data || r.Data || {};
    return sum + getMessageFieldValue(d, field);
  }, 0);
}

const MESSAGE_VIEW_COMBINED = 'combined';
const MESSAGE_VIEW_BREAKDOWN = 'breakdown';

function getMessageTypeMeta(view) {
  if (view === MESSAGE_VIEW_COMBINED || view === MESSAGE_VIEW_BREAKDOWN) return null;
  return MESSAGE_TYPE_FIELDS.find(mt => mt.field === view) || null;
}

function getMessageActivityFields(view) {
  if (view === MESSAGE_VIEW_BREAKDOWN) return null;
  const single = getMessageTypeMeta(view);
  if (single) return [single.field];
  return ROLLUP_ACTIVITY_FIELDS.messages;
}

function DataChart({
  data,
  dataType,
  chartMode = 'combined',
  selectedFacilityIds,
  facilityNamesMap,
  defaultBinning,
  facilityGroupByDefault,
  windowStart,
  windowEnd,
  studyMetric = 'total',
}) {
  const [binning, setBinning] = useState(() => defaultBinning ?? 'month');
  const [messageView, setMessageView] = useState(MESSAGE_VIEW_COMBINED);
  const [facilityBreakdown, setFacilityBreakdown] = useState(false);
  const [topN, setTopN] = useState(5);

  const activityFields = dataType === 'studies'
    ? (STUDY_METRIC_FIELDS[studyMetric] || STUDY_METRIC_FIELDS.total)
    : dataType === 'messages'
      ? getMessageActivityFields(messageView)
      : null;
  const singleMessageType = dataType === 'messages' ? getMessageTypeMeta(messageView) : null;
  const messageBreakdown = dataType === 'messages' && messageView === MESSAGE_VIEW_BREAKDOWN;

  // When the filter panel changes the default, all charts follow until the user picks a different value on a chart.
  useEffect(() => {
    if (defaultBinning != null && defaultBinning !== '') {
      setBinning(defaultBinning);
    }
  }, [defaultBinning]);

  // Referrals/VitalRounds carry both a destination facility (receiving_facility_id)
  // and a sender facility (sending_facility_id — the canonical name produced by
  // DataCacheService normalization; see REFERRAL-FIELD-NAMING.md). Everything
  // else implicitly stays "receiving".
  const supportsSendingSide = dataType === 'referrals' || dataType === 'vitalrounds';
  const [facilityGroupBy, setFacilityGroupBy] = useState(
    supportsSendingSide && facilityGroupByDefault === 'sending' ? 'sending' : 'receiving'
  );
  useEffect(() => {
    if (!supportsSendingSide) {
      setFacilityGroupBy('receiving');
      return;
    }
    if (facilityGroupByDefault === 'sending' || facilityGroupByDefault === 'receiving') {
      setFacilityGroupBy(facilityGroupByDefault);
    }
  }, [facilityGroupByDefault, supportsSendingSide]);
  const [drillDown, setDrillDown] = useState(null);
  const chartRef = useRef(null);
  const chartDataRef = useRef(null);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    if (dataType === 'messages' && messageBreakdown) {
      return buildMessageBreakdownChart(data, binning, windowStart, windowEnd);
    }

    if (facilityBreakdown) {
      return buildFacilityBreakdownChart(data, dataType, binning, topN, facilityNamesMap, facilityGroupBy, windowStart, windowEnd, activityFields);
    }

    const isStackedMode = chartMode === 'stacked' && selectedFacilityIds && selectedFacilityIds.size > 1;

    if (isStackedMode) {
      const facilityData = {};
      Array.from(selectedFacilityIds).forEach(facilityId => {
        facilityData[facilityId] = {};
      });

      data.forEach((record) => {
        const timestamp = record.timestamp || record.data?.created_date;
        if (!timestamp) return;

        // Use the same accessor as getFacilityId so non-referral data types
        // (studies / messages / logins / faxes / study-details) — which
        // only carry facility_id — still attribute correctly under the
        // stacked view.
        const facilityId = getFacilityId(record, 'receiving');
        if (!facilityId || !selectedFacilityIds.has(facilityId)) return;

        const date = parseCalendarDate(timestamp);
        if (!date) return;
        const binKey = getBinKey(date, binning);

        if (!facilityData[facilityId][binKey]) {
          facilityData[facilityId][binKey] = 0;
        }
        facilityData[facilityId][binKey] += getActivityCount(record, dataType, activityFields);
      });

      // Prefer the requested window so empty bins (e.g. weekend gaps) still
      // appear as zero on the line; fall back to whatever bins the data
      // produced when no window was supplied.
      let sortedDates = enumerateBins(windowStart, windowEnd, binning);
      if (!sortedDates) {
        const allDates = new Set();
        Object.values(facilityData).forEach(facilityDates => {
          Object.keys(facilityDates).forEach(date => allDates.add(date));
        });
        sortedDates = Array.from(allDates).sort();
      }

      const datasets = Array.from(selectedFacilityIds).map((facilityId, index) => {
        const counts = sortedDates.map(date => facilityData[facilityId][date] || 0);
        const facilityName = facilityNamesMap?.get(facilityId) || `Facility ${facilityId}`;
        const colorIndex = index % FACILITY_COLORS.length;

        return {
          label: facilityName,
          data: counts,
          borderColor: FACILITY_COLORS[colorIndex].border,
          backgroundColor: FACILITY_COLORS[colorIndex].background,
          tension: 0.3,
          fill: true,
        };
      });

      return { labels: sortedDates, datasets };
    } else {
      const aggregatedData = {};

      data.forEach((record) => {
        const timestamp = record.timestamp || record.data?.created_date;
        if (!timestamp) return;

        const date = parseCalendarDate(timestamp);
        if (!date) return;
        const binKey = getBinKey(date, binning);

        if (!aggregatedData[binKey]) {
          aggregatedData[binKey] = 0;
        }
        aggregatedData[binKey] += getActivityCount(record, dataType, activityFields);
      });

      const enumerated = enumerateBins(windowStart, windowEnd, binning);
      const sortedDates = enumerated || Object.keys(aggregatedData).sort();
      const counts = sortedDates.map(date => aggregatedData[date] || 0);

      return {
        labels: sortedDates,
        datasets: [
          {
            label: singleMessageType?.label ?? getDataTypeLabel(dataType, studyMetric),
            data: counts,
            borderColor: singleMessageType?.border ?? 'rgb(20, 184, 166)',
            backgroundColor: singleMessageType?.background ?? 'rgba(20, 184, 166, 0.15)',
            tension: 0.3,
            fill: true,
          },
        ],
      };
    }
  }, [data, dataType, binning, chartMode, selectedFacilityIds, facilityNamesMap, messageView, facilityBreakdown, topN, facilityGroupBy, windowStart, windowEnd, activityFields, studyMetric, singleMessageType]);

  chartDataRef.current = chartData;

  const handleChartClick = useCallback((event) => {
    const chart = chartRef.current;
    if (!chart) return;

    const nativeEvent = event.nativeEvent || event;
    const elements = chart.getElementsAtEventForMode(nativeEvent, 'nearest', { intersect: true }, false);
    if (elements.length === 0) return;

    const { index, datasetIndex } = elements[0];
    const label = chart.data.labels[index];
    if (label == null) return;

    const currentChartData = chartDataRef.current;
    const chartPointValue = currentChartData?.datasets?.[datasetIndex]?.data?.[index] ?? null;

    // Labels are already canonical bin keys (yyyy-MM-dd); use directly to
    // avoid UTC parsing shifting May 1 → April 30 in US timezones.
    const binKey = typeof label === 'string' && /^\d{4}-\d{2}-\d{2}/.test(label)
      ? label.slice(0, 10)
      : (() => {
          const parsed = parseCalendarDate(label);
          return parsed ? getBinKey(parsed, binning) : String(label);
        })();

    let records = getRecordsForBinKey(data, binKey, binning);
    const periodRowCount = records.length;
    let focusField = null;
    let focusLabel = '';
    if (dataType === 'messages') {
      if (messageView === MESSAGE_VIEW_BREAKDOWN) {
        const mt = MESSAGE_TYPE_FIELDS[datasetIndex];
        if (mt) {
          focusField = mt.field;
          focusLabel = mt.label;
          records = records.filter(r => getMessageFieldValue(r.data || r.Data, mt.field) > 0);
        }
      } else if (singleMessageType) {
        focusField = singleMessageType.field;
        focusLabel = singleMessageType.label;
        records = records.filter(r => getMessageFieldValue(r.data || r.Data, focusField) > 0);
      }
    }

    if (facilityBreakdown && currentChartData?.datasets?.[datasetIndex]) {
      const ds = currentChartData.datasets[datasetIndex];
      if (ds._facilityIds && ds._facilityIds.length > 0) {
        const idSet = new Set(ds._facilityIds);
        records = records.filter(r => {
          const fid = getFacilityId(r, facilityGroupBy);
          return fid && idSet.has(fid);
        });
      }
    }

    if (records.length === 0) return;

    const parts = binKey.split('-');
    const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const friendlyLabel = formatBinLabel(localDate, binning);

    let seriesLabel = '';
    if (facilityBreakdown && currentChartData?.datasets?.[datasetIndex]) {
      seriesLabel = ` — ${currentChartData.datasets[datasetIndex].label}`;
    } else if (focusLabel) {
      seriesLabel = ` — ${focusLabel}`;
    }

    const periodSum = focusField
      ? sumMessageField(getRecordsForBinKey(data, binKey, binning), focusField)
      : null;

    setDrillDown({
      records,
      label: friendlyLabel + seriesLabel,
      focusField,
      focusLabel,
      focusSum: periodSum,
      chartPointValue: typeof chartPointValue === 'number' ? chartPointValue : null,
      periodRowCount: focusField ? periodRowCount : null,
    });
  }, [data, binning, facilityBreakdown, facilityGroupBy, dataType, messageView, singleMessageType]);

  const zoomBy = useCallback((factor) => {
    const c = chartRef.current;
    if (c?.zoom) c.zoom(factor);
  }, []);
  const resetZoom = useCallback(() => {
    const c = chartRef.current;
    if (c?.resetZoom) c.resetZoom();
  }, []);

  const getTimeUnit = () => {
    switch (binning) {
      case 'week':
        return 'week';
      case 'month':
        return 'month';
      case 'day':
      default:
        return 'day';
    }
  };

  const getDisplayFormat = () => {
    switch (binning) {
      case 'week':
        return { week: 'MMM d, yyyy' };
      case 'month':
        return { month: 'MMM yyyy' };
      case 'day':
      default:
        return { day: 'MMM d' };
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `${getDataTypeLabel(dataType, studyMetric)} Over Time${
          messageBreakdown
            ? ' (By Type)'
            : singleMessageType
              ? ` (${singleMessageType.label})`
              : facilityBreakdown
              ? ` (Top ${topN} ${facilityGroupBy === 'sending' ? 'Sending ' : ''}Facilities)`
              : chartMode === 'stacked' && selectedFacilityIds?.size > 1
                ? ' (By Facility)'
                : ''
        }`,
        font: {
          size: 18,
          weight: 'bold',
        },
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            try {
              const date = new Date(context[0].label);
              if (isNaN(date.getTime())) {
                return context[0].label; // Return the original label if date is invalid
              }
              if (binning === 'week') {
                return `Week of ${date.toLocaleDateString()}`;
              } else if (binning === 'month') {
                return format(date, 'MMMM yyyy');
              }
              return date.toLocaleDateString();
            } catch (error) {
              console.error('Error formatting tooltip date:', error);
              return context[0].label;
            }
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          // Require Ctrl (or ⌘) while scrolling so plain page-scroll over a
          // chart doesn't accidentally zoom it. Use the toolbar buttons for
          // mouse-only zooming.
          wheel: {
            enabled: true,
            modifierKey: 'ctrl',
          },
          drag: {
            enabled: false,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: getTimeUnit(),
          displayFormats: getDisplayFormat(),
        },
        title: {
          display: true,
          text: 'Date',
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Count',
        },
        ticks: {
          precision: 0,
        },
      },
    },
  };

  if (!chartData) {
    return <div className="chart-error">No data to display</div>;
  }

  return (
    <div className="data-chart-container">
      <div className="chart-controls">
        <div className="chart-controls-primary">
          <div className="binning-selector">
            <label htmlFor={`chart-binning-${dataType}`}>Group by:</label>
            <select
              id={`chart-binning-${dataType}`}
              value={binning}
              onChange={(e) => setBinning(e.target.value)}
              className="binning-dropdown"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>

          {dataType === 'messages' && (
            <div className="message-type-selector binning-selector">
              <label htmlFor={`chart-msg-view-${dataType}`}>Message type:</label>
              <select
                id={`chart-msg-view-${dataType}`}
                value={messageView}
                disabled={facilityBreakdown}
                onChange={(e) => {
                  const next = e.target.value;
                  setMessageView(next);
                  if (next === MESSAGE_VIEW_BREAKDOWN) setFacilityBreakdown(false);
                }}
                className="binning-dropdown message-type-dropdown"
                title={facilityBreakdown ? 'Disable facility breakdown to filter by message type' : undefined}
              >
                <option value={MESSAGE_VIEW_COMBINED}>All types (combined)</option>
                <option value={MESSAGE_VIEW_BREAKDOWN}>All types (separate lines)</option>
                <optgroup label="Single type">
                  {MESSAGE_TYPE_FIELDS.map(mt => (
                    <option key={mt.field} value={mt.field}>{mt.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}
        </div>

        <div className="chart-controls-secondary">
          <label className="breakdown-toggle">
            <input
              type="checkbox"
              checked={facilityBreakdown}
              onChange={(e) => {
                setFacilityBreakdown(e.target.checked);
                if (e.target.checked) setMessageView(MESSAGE_VIEW_COMBINED);
              }}
            />
            Breakdown by facility
          </label>
          {facilityBreakdown && supportsSendingSide && (
            <div className="facility-side-toggle" role="group" aria-label="Facility side">
              <button
                type="button"
                className={`facility-side-btn ${facilityGroupBy === 'receiving' ? 'active' : ''}`}
                onClick={() => setFacilityGroupBy('receiving')}
                title="Group by the facility that received each referral"
              >
                Receiving
              </button>
              <button
                type="button"
                className={`facility-side-btn ${facilityGroupBy === 'sending' ? 'active' : ''}`}
                onClick={() => setFacilityGroupBy('sending')}
                title="Group by the facility that sent each referral"
              >
                Sending
              </button>
            </div>
          )}
          {facilityBreakdown && (
            <label className="breakdown-toggle topn-control">
              Top
              <input
                type="number"
                min={1}
                max={20}
                value={topN}
                onChange={(e) => setTopN(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
                className="topn-input"
              />
              facilities
            </label>
          )}
          <div className="chart-zoom-controls" role="group" aria-label="Zoom">
            <button type="button" className="chart-zoom-btn" onClick={() => zoomBy(1.2)} title="Zoom in" aria-label="Zoom in">+</button>
            <button type="button" className="chart-zoom-btn" onClick={() => zoomBy(0.8)} title="Zoom out" aria-label="Zoom out">−</button>
            <button type="button" className="chart-zoom-btn chart-zoom-reset" onClick={resetZoom} title="Reset zoom" aria-label="Reset zoom">Reset</button>
          </div>
        </div>
      </div>
      <p className="chart-hint">
        {messageBreakdown && dataType === 'messages'
          ? 'Click a data point to drill down'
          : 'Click a point to drill down · +/− buttons or Ctrl+scroll to zoom · Drag to pan'}
      </p>
      <div className="chart-canvas-area">
        <Line ref={chartRef} data={chartData} options={options} onClick={handleChartClick} />
      </div>

      {drillDown && (
        <DrillDownModal
          records={drillDown.records}
          dataType={dataType}
          binLabel={drillDown.label}
          focusField={drillDown.focusField}
          focusLabel={drillDown.focusLabel}
          focusSum={drillDown.focusSum}
          chartPointValue={drillDown.chartPointValue}
          periodRowCount={drillDown.periodRowCount}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
}

function getRecordsForBinKey(data, clickedBinKey, binning) {
  if (!data || !clickedBinKey) return [];

  return data.filter(record => {
    const timestamp = record.timestamp || record.data?.created_date || record.Data?.created_date;
    if (!timestamp) return false;
    const date = parseCalendarDate(timestamp);
    if (!date) return false;
    const recordBinKey = getBinKey(date, binning);
    return recordBinKey === clickedBinKey;
  });
}

function formatBinLabel(binDate, binning) {
  if (!binDate || isNaN(binDate.getTime())) return 'Unknown';
  switch (binning) {
    case 'month':
      return format(binDate, 'MMMM yyyy');
    case 'week':
      return `Week of ${format(startOfWeek(binDate, { weekStartsOn: 0 }), 'MMM d, yyyy')}`;
    case 'day':
    default:
      return format(binDate, 'MMM d, yyyy');
  }
}

function getFacilityId(record, side = 'receiving') {
  const d = record.data || record.Data;
  // Sending = the actual sender's facility (sending_facility_id).
  // See REFERRAL-FIELD-NAMING.md.
  const id = side === 'sending'
    ? d?.sending_facility_id
    : (d?.receiving_facility_id ?? d?.facility_id);
  if (id == null) return null;
  return String(Math.round(parseFloat(String(id))));
}

function buildFacilityBreakdownChart(data, dataType, binning, topN, facilityNamesMap, side = 'receiving', windowStart, windowEnd, activityFields = null) {
  const facilityCounts = {};
  data.forEach(record => {
    const fid = getFacilityId(record, side);
    if (!fid) return;
    facilityCounts[fid] = (facilityCounts[fid] || 0) + getActivityCount(record, dataType, activityFields);
  });

  const sorted = Object.entries(facilityCounts).sort((a, b) => b[1] - a[1]);
  const topIds = new Set(sorted.slice(0, topN).map(([id]) => id));
  const hasOther = sorted.length > topN;

  const facilityBins = {};
  topIds.forEach(id => { facilityBins[id] = {}; });
  if (hasOther) facilityBins['__other__'] = {};

  data.forEach(record => {
    const timestamp = record.timestamp || record.data?.created_date;
    if (!timestamp) return;
    const fid = getFacilityId(record, side);
    if (!fid) return;

    const date = parseCalendarDate(timestamp);
    if (!date) return;
    const binKey = getBinKey(date, binning);
    const bucket = topIds.has(fid) ? fid : '__other__';

    if (!facilityBins[bucket]) facilityBins[bucket] = {};
    facilityBins[bucket][binKey] = (facilityBins[bucket][binKey] || 0) + getActivityCount(record, dataType, activityFields);
  });

  let sortedDates = enumerateBins(windowStart, windowEnd, binning);
  if (!sortedDates) {
    const allDates = new Set();
    Object.values(facilityBins).forEach(bins => Object.keys(bins).forEach(d => allDates.add(d)));
    sortedDates = Array.from(allDates).sort();
  }

  const datasets = [];
  let colorIdx = 0;

  sorted.slice(0, topN).forEach(([fid]) => {
    const name = facilityNamesMap?.get(fid) || `Facility ${fid}`;
    const ci = colorIdx++ % FACILITY_COLORS.length;
    datasets.push({
      label: name,
      data: sortedDates.map(d => facilityBins[fid]?.[d] || 0),
      borderColor: FACILITY_COLORS[ci].border,
      backgroundColor: FACILITY_COLORS[ci].background,
      tension: 0.3,
      fill: false,
      _facilityIds: [fid],
    });
  });

  if (hasOther) {
    const otherCount = sorted.slice(topN).reduce((sum, [, c]) => sum + c, 0);
    const otherFacilities = sorted.length - topN;
    const otherIds = sorted.slice(topN).map(([id]) => id);
    datasets.push({
      label: `Other (${otherFacilities} facilities, ${otherCount.toLocaleString()} total)`,
      data: sortedDates.map(d => facilityBins['__other__']?.[d] || 0),
      borderColor: 'rgb(156, 163, 175)',
      backgroundColor: 'rgba(156, 163, 175, 0.1)',
      borderDash: [5, 5],
      tension: 0.3,
      fill: false,
      _facilityIds: otherIds,
    });
  }

  return { labels: sortedDates, datasets };
}

function buildMessageBreakdownChart(data, binning, windowStart, windowEnd) {
  const buckets = {};
  MESSAGE_TYPE_FIELDS.forEach(mt => { buckets[mt.field] = {}; });

  data.forEach((record) => {
    const recordData = record.data || record.Data;
    const timestamp = record.timestamp || recordData?.created_date;
    if (!timestamp) return;

    const date = parseCalendarDate(timestamp);
    if (!date) return;
    const binKey = getBinKey(date, binning);

    MESSAGE_TYPE_FIELDS.forEach(mt => {
      const val = getMessageFieldValue(recordData, mt.field);
      if (!buckets[mt.field][binKey]) buckets[mt.field][binKey] = 0;
      buckets[mt.field][binKey] += val;
    });
  });

  let sortedDates = enumerateBins(windowStart, windowEnd, binning);
  if (!sortedDates) {
    const allDates = new Set();
    Object.values(buckets).forEach(b => Object.keys(b).forEach(d => allDates.add(d)));
    sortedDates = Array.from(allDates).sort();
  }

  const datasets = MESSAGE_TYPE_FIELDS
    .map(mt => {
      const counts = sortedDates.map(d => buckets[mt.field][d] || 0);
      const total = counts.reduce((a, b) => a + b, 0);
      if (total === 0) return null;
      return {
        label: mt.label,
        data: counts,
        borderColor: mt.border,
        backgroundColor: mt.background,
        tension: 0.3,
        fill: true,
      };
    })
    .filter(Boolean);

  return { labels: sortedDates, datasets };
}

export default DataChart;
